import { useEffect } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchBudgetNoteWorkspace } from '../../services/budgetNoteService';
import type { BudgetNoteWorkspaceSnapshot } from '../../utils/budgetNoteWorkspaceCache';
import { loadBudgetNoteWorkspaceCache } from '../../utils/budgetNoteWorkspaceCache';
import { consumeBudgetNoteLoginHeadSyncPending } from '../../utils/budgetNoteLoginHeadSync';
import { noteKeys } from '../keys';
import { queryClient } from '../queryClient';
import { lsInitialDataOptions } from './lsInitialData';

export type UseBudgetNoteWorkspaceQueryParams = {
  userId: string;
  noteWorkspaceId: string | null;
  enabled?: boolean;
  forceRefresh?: boolean;
};

export function useBudgetNoteWorkspaceQuery(
  params: UseBudgetNoteWorkspaceQueryParams
): UseQueryResult<BudgetNoteWorkspaceSnapshot> {
  const { userId, noteWorkspaceId, enabled = true, forceRefresh = false } = params;
  const ls = loadBudgetNoteWorkspaceCache(userId, noteWorkspaceId);

  // 로그인·새로고침 직후 플래그가 있을 때만 head 비교(필요 시 풀 로드)
  useEffect(() => {
    if (!enabled || !userId) return;
    if (!consumeBudgetNoteLoginHeadSyncPending()) return;
    void queryClient.fetchQuery({
      queryKey: noteKeys.budgetWorkspace(userId, noteWorkspaceId),
      queryFn: () => fetchBudgetNoteWorkspace(noteWorkspaceId, { forceRefresh: false }),
    });
  }, [enabled, userId, noteWorkspaceId]);

  return useQuery<BudgetNoteWorkspaceSnapshot>({
    queryKey: noteKeys.budgetWorkspace(userId, noteWorkspaceId),
    enabled: enabled && !!userId,
    ...lsInitialDataOptions(ls.found, ls.data, ls.savedAt),
    queryFn: () => fetchBudgetNoteWorkspace(noteWorkspaceId, { forceRefresh }),
  });
}
